import React from 'react';
import classNames from 'classnames';

import css from './IconCard.module.css';

const BRAND_AMEX = 'amex';
const BRAND_DINERS = 'diners';
const BRAND_DISCOVERY = 'discovery';
const BRAND_JCB = 'jcb';
const BRAND_MASTERCARD = 'mastercard';
const BRAND_UNIONPAY = 'unionpay';
const BRAND_VISA = 'visa';

// new icons
const info = 'info';
const infoRound = 'infoRound';
const tick = 'tick';
const rightArrow = 'rightArrow';
const exploreRoundCta = 'exploreRoundCta';
const plus = 'plus';
const calendar = 'calendar';
const eye = 'eye';
const eyeOff = 'eyeOff';
const close = 'close';

/**
 * Payment card icon.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {'amex' | 'diners' | 'discovery' | 'jcb' | 'mastercard' | 'unionpay' | 'visa'} props.brand payment card provider
 * @returns {JSX.Element} SVG icon
 */
const IconCard = props => {
  const { className, rootClassName, brand = 'default' } = props;
  const classes = classNames(rootClassName || css.root, className);
  const roleInfo = {
    role: 'img',
    ['aria-label']: brand,
  };

  switch (brand) {
    case BRAND_AMEX:
      return (
        <svg
          className={classes}
          height="16"
          viewBox="0 0 24 16"
          width="24"
          xmlns="http://www.w3.org/2000/svg"
          {...roleInfo}
        >
          <g fill="none" fillRule="evenodd">
            <rect fill="#016fd0" height="16" rx="2" width="24" />
            <path
              d="M13.764 13.3939V7.6925l10.1476.009v1.575l-1.1729 1.2534 1.1729 1.2648v1.6083H22.039l-.9952-1.0981-.9881 1.1023z"
              fill="#fffffe"
            />
            <path
              d="M14.4418 12.7688V8.3199h3.7723v1.0249h-2.5509v.6957h2.49v1.0078h-2.49v.6833h2.5509v1.0372z"
              fill="#016fd0"
            />
            <path
              d="M18.1953 12.7688l2.0873-2.227L18.1952 8.32h1.6156l1.2755 1.41 1.2791-1.41h1.5462v.035l-2.0429 2.1868 2.0429 2.1639v.0631h-1.5617l-1.2981-1.4242-1.2848 1.4242z"
              fill="#016fd0"
            />
            <path
              d="M14.2373 2.632h2.4461l.8591 1.9508V2.632h3.0199l.5207 1.4615.5226-1.4615h2.3059v5.7013H11.7251z"
              fill="#fffffe"
            />
            <g fill="#016fd0">
              <path d="M14.7005 3.2514l-1.974 4.4451h1.3539l.3724-.8901h2.018l.372.8901h1.3877l-1.9658-4.4451zm.1696 2.5574l.592-1.415.5916 1.415z" />
              <path d="M18.212 7.6957v-4.445l1.903.0065.9794 2.7327.9856-2.7392h1.8316v4.445l-1.1786.0105V4.6528l-1.1126 3.043h-1.0758L19.409 4.6423v3.0533z" />
            </g>
          </g>
        </svg>
      );
    case BRAND_DINERS:
      return (
        <svg
          className={classes}
          width="24"
          height="16"
          viewBox="0 0 24 16"
          xmlns="http://www.w3.org/2000/svg"
          {...roleInfo}
        >
          <g fillRule="nonzero" fill="none">
            <path
              d="M21.9972 15.75h.0022c.955.0081 1.7387-.7727 1.7506-1.7458V2.0063c-.0038-.4706-.1911-.9201-.5203-1.2495a1.7197 1.7197 0 0 0-1.2325-.5067H2.0006C1.5411.246 1.0986.4283.7703.7568.441 1.0862.2538 1.5357.25 2.0043v11.9894c.0038.4706.1911.9201.5203 1.2495a1.7197 1.7197 0 0 0 1.2325.5067h19.9944zm-.001.5h-.0011.0021-.001z"
              strokeOpacity=".2"
              stroke="#000"
              strokeWidth=".5"
              fill="#FFF"
            />
            <path
              d="M10.0021 2.0518v-.016h4v.016c3.1354.2294 5.5621 2.8402 5.5621 5.984s-2.4267 5.7546-5.562 5.984v.016h-4v-.016C6.8666 13.7904 4.44 11.1796 4.44 8.0358s2.4267-5.7546 5.5621-5.984z"
              fill="#0165AC"
            />
            <path
              d="M11.6021 11.4278a3.6 3.6 0 0 0 0-6.784v6.784zm-2.4-6.784a3.6 3.6 0 0 0 0 6.784v-6.784zm1.2 8.592c-2.8719 0-5.2-2.3281-5.2-5.2s2.3281-5.2 5.2-5.2 5.2 2.3281 5.2 5.2-2.3281 5.2-5.2 5.2z"
              fill="#FFF"
            />
          </g>
        </svg>
      );
    case BRAND_DISCOVERY:
      return (
        <svg
          className={classes}
          width="24"
          height="16"
          viewBox="0 0 24 16"
          xmlns="http://www.w3.org/2000/svg"
          {...roleInfo}
        >
          <g fillRule="nonzero" fill="none">
            <path
              d="M21.9972 15.75h.0022c.955.0081 1.7387-.7727 1.7506-1.7458V2.0063c-.0038-.4706-.1911-.9201-.5203-1.2495a1.7197 1.7197 0 0 0-1.2325-.5067H2.0006C1.5411.246 1.0986.4283.7703.7568.441 1.0862.2538 1.5357.25 2.0043v11.9894c.0038.4706.1911.9201.5203 1.2495a1.7197 1.7197 0 0 0 1.2325.5067h19.9944zm-.001.5h-.0011.0021-.001z"
              strokeOpacity=".2"
              stroke="#000"
              strokeWidth=".5"
              fill="#FFF"
            />
            <path
              d="M12.6124 16h9.3848A1.9862 1.9862 0 0 0 24 14.0302v-2.3586a38.7393 38.7393 0 0 1-11.3876 4.3282z"
              fill="#F27712"
            />
            <path
              d="M23.1724 9.2965H22.32l-.96-1.2662h-.091v1.2662h-.6952V6.1517H21.6c.8028 0 1.2662.331 1.2662.9268 0 .4883-.2896.8028-.811.9021l1.1172 1.3159zm-1.0262-2.1931c0-.3062-.2317-.4635-.662-.4635h-.2152v.9518h.1986c.4469 0 .6786-.1656.6786-.4883zm-4.0055-.9517h1.9696v.5296H18.836v.7035h1.2248v.5379h-1.2248v.8524h1.2744v.5297h-1.9696V6.1517zm-2.2345 3.2275L14.4 6.1434h.7614l.9517 2.1186.96-2.1186h.7448l-1.5227 3.2358h-.389zm-6.298-.0082c-1.0592 0-1.8868-.72-1.8868-1.6552 0-.9104.8441-1.647 1.9034-1.647.298 0 .5462.058.8524.1904v.7283a1.2414 1.2414 0 0 0-.869-.3558c-.662 0-1.1668.48-1.1668 1.084 0 .6373.4965 1.0925 1.2 1.0925.3145 0 .5545-.0993.8358-.3476v.7283c-.3144.1324-.5793.182-.869.182zm-2.102-1.0345c0 .6124-.5048 1.0345-1.2331 1.0345-.5297 0-.9103-.1821-1.2331-.5959l.4552-.389c.1572.2814.422.4221.753.4221.3146 0 .538-.1903.538-.4386 0-.1407-.0662-.2483-.2069-.331a2.88 2.88 0 0 0-.48-.1821c-.6538-.207-.8772-.4304-.8772-.869 0-.513.48-.902 1.109-.902.3972 0 .753.124 1.051.3558l-.3642.4138a.7614.7614 0 0 0-.5627-.2483c-.298 0-.5131.149-.5131.3476 0 .1655.1241.2566.5379.3973.7945.2482 1.0262.48 1.0262.993v-.0082zm-3.418-2.1848h.6952v3.153h-.6951v-3.153zm-2.2344 3.153H.8276v-3.153h1.0262c1.1255 0 1.9034.6455 1.9034 1.5724 0 .4717-.2317.9186-.6372 1.2165-.3476.2483-.7366.3642-1.2745.3642h.0083zm.811-2.3668c-.2317-.1821-.4965-.2483-.9517-.2483h-.1903V8.775h.1903c.4469 0 .7283-.0828.9517-.2483.24-.1986.3807-.4965.3807-.8027 0-.3062-.1407-.5959-.3807-.7862z"
              fill="#000"
            />
            <path
              d="M12.4138 6.0689c-.9104 0-1.6552.7283-1.6552 1.6303 0 .96.7117 1.68 1.6552 1.68.9269 0 1.6552-.7282 1.6552-1.6551 0-.927-.72-1.6552-1.6552-1.6552z"
              fill="#F27712"
            />
          </g>
        </svg>
      );
    case BRAND_JCB:
      return (
        <svg
          className={classes}
          height="16"
          viewBox="0 0 23 16"
          width="23"
          xmlns="http://www.w3.org/2000/svg"
          {...roleInfo}
        >
          <g fill="none">
            <path
              d="M.2054 16h4.5178c1.0278 0 2.259-1.1947 2.259-2.1333V0h-4.518C1.4366 0 .2055 1.1947.2055 3.2z"
              fill="#047ab1"
            />
            <path
              d="M2.7692 10.816a9.7709 9.7709 0 0 1-2.5638-.3413V9.3013c.6467.3777 1.386.5909 2.146.6187.8696 0 1.3553-.576 1.3553-1.3653V5.3333h2.146v3.2214c0 1.2586-.7003 2.2613-3.0835 2.2613z"
              fill="#fff"
            />
            <path
              d="M8.1116 16h4.5179c1.0278 0 2.2589-1.1947 2.2589-2.1333V0h-4.5179C9.3427 0 8.1116 1.1947 8.1116 3.2z"
              fill="#d42d06"
            />
            <path
              d="M8.1116 6.08c.6551-.5973 1.7846-.9707 3.6143-.8853.994.0426 2.033.32 2.033.32v1.184c-.5943-.3395-1.2623-.5474-1.954-.608C10.4158 5.9733 9.5687 6.7093 9.5687 8s.8471 2.0267 2.2364 1.92c.692-.066 1.3596-.2774 1.954-.6187v1.1734s-1.0392.288-2.0331.3306c-1.8297.0854-2.9592-.288-3.6143-.8853z"
              fill="#fff"
            />
            <path
              d="M16.0179 16h4.5178c1.0278 0 2.259-1.1947 2.259-2.1333V0h-4.518C17.249 0 16.018 1.1947 16.018 3.2z"
              fill="#67b637"
            />
            <path
              d="M21.6652 9.28c0 .8533-.7003 1.3867-1.6377 1.3867h-4.0096V5.3333h3.6481l.2598.0107c.8245.0427 1.4344.5013 1.4344 1.2907 0 .6186-.4179 1.152-1.1746 1.28v.032c.8358.0533 1.4796.5546 1.4796 1.3333zM18.785 6.176a1.0871 1.0871 0 0 0-.1468-.0107H17.283v1.344h1.5021c.2824-.064.5196-.3093.5196-.672 0-.3626-.2372-.608-.5196-.6613zm.1695 2.176a1.0276 1.0276 0 0 0-.1808-.0107H17.283v1.4614h1.4908l.1808-.0214c.2823-.064.5195-.3413.5195-.7146 0-.3734-.2259-.64-.5195-.7147z"
              fill="#fff"
            />
          </g>
        </svg>
      );
    case BRAND_MASTERCARD:
      return (
        <svg
          className={classes}
          height="16"
          viewBox="0 0 24 16"
          width="24"
          xmlns="http://www.w3.org/2000/svg"
          {...roleInfo}
        >
          <g fill="none" fillRule="evenodd">
            <rect fill="#252525" height="16" rx="2" width="24" />
            <circle cx="9" cy="8" fill="#eb001b" r="5" />
            <circle cx="15" cy="8" fill="#f79e1b" r="5" />
            <path
              d="M12 3.9996c1.2144.9122 2 2.3646 2 4.0004s-.7856 3.0882-2 4.0004C10.7856 11.0882 10 9.6358 10 8s.7856-3.0882 2-4.0004z"
              fill="#ff5f00"
            />
          </g>
        </svg>
      );
    case BRAND_UNIONPAY:
      return (
        <svg
          className={classes}
          height="16"
          viewBox="0 0 24 16"
          width="24"
          xmlns="http://www.w3.org/2000/svg"
          {...roleInfo}
        >
          <g fill="none">
            <path
              d="M4.5459 0h5.7938c.8087 0 1.3117.7257 1.123 1.619L8.7655 14.3675c-.1904.8902-1.0002 1.6165-1.8095 1.6165H1.1627c-.8076 0-1.3118-.7263-1.1231-1.6165L2.7381 1.619C2.9268.7257 3.7361 0 4.546 0"
              fill="#dd2423"
            />
            <path
              d="M9.8576 0h6.6627c.8086 0 .444.7257.2537 1.619l-2.697 12.7485c-.1892.8902-.1302 1.6165-.9405 1.6165H6.4738c-.8103 0-1.3118-.7263-1.1214-1.6165L8.0493 1.619C8.2408.7257 9.0483 0 9.8576 0"
              fill="#16315e"
            />
            <path
              d="M16.256 0h5.7938c.8098 0 1.3129.7257 1.1226 1.619l-2.697 12.7485c-.1903.8902-1.0006 1.6165-1.8104 1.6165h-5.791c-.8104 0-1.313-.7263-1.1232-1.6165L14.4482 1.619C14.637.7257 15.4457 0 16.256 0"
              fill="#036862"
            />
            <path
              d="M6.059 4.0856c-.5958.0067-.7717 0-.828-.0146-.0216.113-.4236 2.1546-.4247 2.1563-.0866.413-.1496.7075-.3635.8976-.1215.1105-.2633.1639-.4276.1639-.2643 0-.4182-.1445-.444-.4184l-.005-.094s.0805-.5533.0805-.5564c0 0 .422-1.8605.4975-2.1064a.1945.1945 0 0 0 .0061-.028c-.8213.008-.967 0-.977-.0146-.0055.02-.0258.1354-.0258.1354l-.4308 2.097-.037.1778-.0716.5817c0 .1725.0307.3134.092.4325.1963.3776.7563.4342 1.073.4342.4081 0 .791-.0955 1.0497-.2698.449-.292.5666-.7486.6713-1.1544l.0486-.2081s.4347-1.9323.5085-2.1837c.0029-.014.004-.0213.0078-.028zm1.479 1.5588c-.1048 0-.2963.028-.4683.1208-.0624.0353-.1215.076-.1838.1166l.0562-.2234-.0307-.0376c-.3648.0813-.4464.0922-.7834.1444l-.0282.0207c-.0391.357-.0739.6255-.219 1.3274a90.3116 90.3116 0 0 1-.17.778l.0156.0327c.3453-.02.45-.02.7502-.0146l.0242-.029c.0382-.215.0431-.2654.1276-.7009.0396-.2064.1224-.66.1632-.8215.075-.0383.149-.0759.2196-.0759.1682 0 .1477.1615.1412.2259-.0072.108-.0685.4609-.1313.7639l-.042.1955c-.0292.1445-.0612.2848-.0905.428l.0127.0286c.3404-.02.4442-.02.7348-.0146l.0342-.029c.0525-.3358.0679-.4257.1611-.9146l.0469-.2246c.091-.4397.1368-.6626.068-.8442-.073-.2035-.2478-.2526-.4083-.2526zm1.6521.4603c-.181.0383-.2963.0637-.411.0802-.1136.02-.2245.0383-.3993.065l-.0139.0138-.0127.011c-.0182.1434-.0309.2673-.055.413-.0205.1506-.052.3218-.1033.5677-.0397.1882-.0601.2538-.0827.32-.022.0663-.0463.1307-.091.3158l.0105.0171.0087.0157c.1634-.0085.2703-.0146.3802-.0157.1097-.0043.2234 0 .3994.001l.0154-.0137.0165-.0153c.0254-.167.0292-.2119.0447-.2933.0154-.0874.042-.2083.107-.5314.0309-.1517.0652-.303.0971-.4578.0332-.1542.068-.306.101-.4578l-.005-.0183zm.0039-.6206c-.1644-.1068-.453-.0729-.6471.0746-.1936.1446-.2157.3498-.0518.458.1615.1038.4512.0729.6436-.0759.1932-.1477.2174-.351.0553-.4567zm.994 2.4734c.3326 0 .6735-.101.9301-.4003.1974-.2428.288-.6041.3193-.7529.1021-.4931.0226-.7233-.0772-.8635-.1517-.2138-.4197-.2824-.6978-.2824-.1672 0-.5654.0182-.8765.334-.2234.2277-.3267.5368-.389.833-.0628.302-.135.8454.319 1.0477.14.0662.342.0844.4722.0844zm-.026-1.1095c.0768-.3734.1673-.6868.3983-.6868.181 0 .1942.2332.1137.6078-.0144.0832-.0805.3924-.1699.524-.0624.0972-.1363.1561-.2179.1561-.0243 0-.1688 0-.171-.2361-.0011-.1166.0205-.2357.0469-.365zm2.1068 1.0615l.026-.0291c.0368-.215.0429-.2655.1245-.7008.0408-.2065.1253-.66.165-.8216.075-.0384.1478-.076.2207-.076.167 0 .1467.1615.14.2259-.006.1082-.0673.4609-.1313.7639l-.0396.1955c-.0304.1446-.0635.2848-.0927.4282l.0127.0286c.3415-.02.4413-.02.7337-.0146l.0353-.0291c.0512-.336.065-.4258.161-.9146l.0458-.2247c.0916-.4397.1379-.6624.07-.844-.0749-.2035-.2509-.2526-.4092-.2526-.1049 0-.2974.0278-.4684.1208a3.0577 3.0577 0 0 0-.1826.1166l.0524-.2234-.028-.0378c-.3647.0814-.448.0923-.7846.1446l-.0258.0206c-.0408.3571-.074.6254-.219 1.3275a95.9633 95.9633 0 0 1-.17.7779l.0155.0328c.3458-.02.449-.02.7486-.0146zm2.5088.0145c.0215-.1154.149-.799.1501-.799 0 0 .1086-.5017.1152-.5199 0 0 .0342-.0522.0684-.0729h.0503c.4743 0 1.01 0 1.4298-.34.2857-.2332.481-.5775.5681-.996a1.6492 1.6492 0 0 0 .0393-.3466c0-.1603-.0292-.3189-.1137-.4428-.214-.3297-.6404-.3358-1.1326-.3383l-.2426.0025c-.63.0086-.8826.0061-.9864-.0079-.0088.0505-.0253.1404-.0253.1404s-.2256 1.1513-.2256 1.1531l-.5655 2.5632c.55-.0073.7756-.0073.8705.0042zm.4181-2.0452s.24-1.149.2388-1.1446l.0078-.059.0034-.0448.0959.0108s.4948.0468.5064.048c.1953.0832.2758.2976.2196.5774-.0513.2558-.202.4708-.3956.5746-.1594.088-.3547.0953-.556.0953h-.1301zm1.494.9905c-.0635.2975-.1364.841.3154 1.0347.144.0674.2731.0875.4043.0802.1385-.0082.2669-.0847.3858-.1948l-.0322.1359.0205.029c.325-.015.4258-.015.7778-.012l.032-.0268c.0513-.3327.0998-.6557.2334-1.2922.065-.3048.13-.6067.1968-.9103l-.0104-.0334c-.3635.0741-.4607.09-.8103.1445l-.0266.0238c-.0035.031-.0072.0607-.0106.0904-.0543-.0967-.1331-.1793-.2547-.2307-.1555-.0674-.5207.0194-.8346.334-.2206.2247-.3265.5325-.3867.8277zm.7633.0182c.0778-.3668.1672-.677.3988-.677.1465 0 .2236.1487.208.4024-.0125.0633-.026.13-.0419.2054-.0232.109-.0483.217-.0727.3252-.0249.074-.0538.1438-.0856.1903-.0596.093-.2014.1506-.283.1506-.0232 0-.166 0-.171-.232-.0011-.1154.0205-.2344.0474-.365zm3.9867-1.2109l-.0281-.0353c-.3597.0802-.4248.093-.7552.1421l-.0243.0267c-.0012.0044-.0021.011-.0038.0171l-.0012-.006c-.246.6248-.2388.49-.439.9818-.0011-.0224-.0011-.0363-.0023-.0601l-.0501-1.0663-.0315-.0353c-.3768.0802-.3857.093-.7336.1421l-.0271.0267c-.0039.0128-.0039.0268-.0061.042l.0022.0055c.0435.2446.033.19.0767.5762.0204.1895.0475.3801.0678.5673.0343.3132.0535.4674.0955.9455-.235.4268-.2907.5884-.5169.963l.0016.0037-.1593.2774c-.0182.0292-.0348.0492-.058.0578-.0253.0138-.0584.0163-.1043.0163h-.0882l-.1312.4804.45.0085c.2642-.0012.4303-.1372.5197-.32l.283-.5339h-.0045l.0298-.0376c.1904-.4511 1.6384-3.1856 1.6384-3.1856zm-4.7501 6.3088h-.191l.7067-2.573h.2344l.0745-.265.0072.2947c-.0088.1822.1214.3437.4634.317h.3956l.1361-.4954h-.1488c-.0856 0-.1253-.0238-.1203-.0748l-.0072-.2999h-.7325v.0016c-.2369.0054-.944.025-1.0873.067-.1732.049-.3558.1937-.3558.1937l.0717-.2654h-.6852l-.1428.5265-.716 2.6123h-.139l-.1363.4919h1.3647l-.0457.164h.6724l.0447-.164h.1886zm-.56-2.0502c-.1097.0334-.3139.1348-.3139.1348l.1816-.6576h.5444l-.1313.4791s-.1682.011-.2807.0437zm.0105.9394s-.171.0237-.2836.0516c-.1108.037-.3187.1536-.3187.1536l.1876-.6843h.5472zm-.305 1.1168h-.5461l.1583-.5775h.5444zm1.315-1.5959h.7872l-.113.4033h-.7977l-.1197.4408h.6979l-.5285.8191c-.037.0602-.0702.0815-.107.0984-.037.0207-.0856.045-.1418.045h-.1936l-.133.4828h.5064c.2632 0 .4187-.1318.5335-.3048l.3624-.546.0778.5544c.0165.1038.0843.1646.1302.1882.0507.028.103.076.177.0832.0794.0037.1368.0067.1749.0067h.2488l.1494-.5404h-.0981c-.0564 0-.1534-.0104-.1699-.0298-.0165-.0237-.0165-.06-.0254-.1154l-.079-.5556h-.3232l.1418-.1858h.796l.1225-.4408h-.737l.1148-.4033h.7347l.1363-.4973h-2.1906zm-6.6482 1.7082l.1837-.6728h.7551l.138-.5004h-.7558l.1153-.4141h.7386l.1369-.4846h-1.848l-.134.4846h.4198l-.112.414h-.421l-.1395.509h.4197l-.2449.8902c-.033.1178.0156.1627.0464.2175.0315.0533.0634.0886.1351.1086.074.0182.1247.0291.1936.0291h.8513l.1517-.5544-.3774.0571c-.0729 0-.2747-.0096-.2526-.0838zm.0866-3.2208l-.1914.3807c-.041.0831-.0778.1348-.111.1585-.0292.02-.0871.0285-.171.0285h-.0998l-.1335.487h.3316c.1594 0 .2819-.0643.3403-.0965.0629-.037.0794-.0159.128-.0675l.112-.1068h1.0354l.1374-.507h-.758l.1324-.277zm1.5286 3.2306c-.0177-.028-.005-.0773.022-.1799l.283-1.0311h1.0068c.1467-.0023.2526-.0042.3215-.0096.074-.0086.1545-.0377.2422-.0899.0905-.0547.1369-.1124.176-.1786.0436-.066.1137-.2107.1738-.4336l.3557-1.3049-1.0447.0067s-.3217.0522-.4633.11c-.143.0643-.3471.244-.3471.244l.0943-.3577h-.6454l-.9035 3.2986c-.032.128-.0535.221-.0585.2769-.0017.0601.069.1197.1147.1646.054.0449.134.0376.2107.0449.0806.0067.1953.0109.3536.0109h.496l.1522-.5659-.444.0462a.1138.1138 0 0 1-.096-.0516zm.4876-1.9074h1.0575l-.0673.2319c-.0094.0054-.032-.0115-.1396.0025h-.9157zm.2118-.778h1.0664l-.0767.2794s-.5026-.0054-.583.0109c-.3543.0675-.5611.2758-.5611.2758zm.8021 1.7866c-.0087.0347-.0226.0558-.042.0717-.0214.0152-.0561.0206-.108.0206h-.1506l.0089-.2824H12.7l-.0254 1.3809c-.001.0996.0078.1573.074.2035.0662.0576.2703.065.545.065h.3927l.1418-.517-.342.0207-.1136.0073c-.0155-.0073-.0304-.014-.0469-.0321-.0144-.0157-.0387-.0061-.0347-.1057l.0027-.354.3585-.0163c.1936 0 .2764-.0694.347-.1354.0674-.0633.0894-.136.1148-.2345l.0602-.3134h-.4927z"
              fill="#fefefe"
            />
          </g>
        </svg>
      );
    case BRAND_VISA:
      return (
        <svg
          className={classes}
          width="24"
          height="16"
          viewBox="0 0 24 16"
          xmlns="http://www.w3.org/2000/svg"
          {...roleInfo}
        >
          <g fillRule="nonzero" fill="none">
            <rect
              strokeOpacity=".2"
              stroke="#000"
              strokeWidth=".5"
              fill="#FFF"
              x=".25"
              y=".25"
              width="23.5"
              height="15.5"
              rx="2"
            />
            <path
              d="M2.78773262 5.91443732C2.26459089 5.62750595 1.6675389 5.39673777 1 5.23659312l.0280005-.12471102h2.73697872c.37098332.01300346.67201191.12462375.76996714.51882925l.59482061 2.83587917.18221641.85413565L6.97796639 5.1118821h1.79882257l-2.67390785 6.1656463H4.30396552l-1.5162329-5.36309108zm7.31224258 5.36963648H8.39882877l1.06401886-6.1721917H11.163901l-1.0639258 6.1721917zm6.1668069-6.02129922l-.2313529 1.3328108-.1538632-.06554092c-.3078194-.12479829-.7141522-.2495093-1.2671852-.2363313-.6716398 0-.9728545.26905813-.9798314.53174543 0 .28895604.3643786.47938251.9593659.76179318.9801104.43993579 1.4347231.97831385 1.4279323 1.68093694-.0137677 1.28044789-1.1758348 2.10786929-2.9607967 2.10786929-.7631763-.0066327-1.4982591-.1579615-1.8972429-.3284901l.2380507-1.38552277.224004.09861682c.5530331.23004776.9169465.32831555 1.5961213.32831555.4897761 0 1.0149017-.19060107 1.0215995-.60409358 0-.26923267-.2237249-.46637906-.8818761-.76842582-.6439184-.29558867-1.5048639-.78797464-1.4909101-1.67447884C11.8780532 5.84042483 13.0610509 5 14.7409877 5c.6580581 0 1.1902536.13788902 1.5257944.26277458zm2.2609703 3.83471102h1.4139786c-.0699548-.30859213-.3921-1.78601186-.3921-1.78601186l-.1188858-.5318327c-.0840015.22978594-.2308878.60409353-.2238179.59091553 0 0-.5390794 1.3723448-.6791749 1.72692903zm2.0998512-3.9856035L22 11.2839865h-1.5750977s-.1541422-.7091684-.2029803-.9258637h-2.1841317c-.0631639.1640705-.3570296.9258637-.3570296.9258637h-1.7849619l2.5268355-5.65999506c.1750729-.40057632.4833574-.51210934.889132-.51210934h1.3158373z"
              fill="#171E6C"
            />
          </g>
        </svg>
      );

    // new icons
    case info:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="M7.33936 7.49902H8.10634V10.3335"
            stroke="#FFFEF4"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
          <path
            d="M7.33301 10.3332H8.87321"
            stroke="#FFFEF4"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
          <path
            d="M8.06661 5.49607C8.06661 5.58816 7.99196 5.66281 7.89988 5.66281C7.80779 5.66281 7.73314 5.58816 7.73314 5.49607C7.73314 5.40398 7.80779 5.32933 7.89988 5.32933"
            stroke="#FFFEF4"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
          <path
            d="M7.90001 5.32945C7.99209 5.32945 8.06674 5.4041 8.06674 5.49619"
            stroke="#FFFEF4"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
          <rect
            x="1.99756"
            y="1.99658"
            width="12.005"
            height="12.005"
            rx="4"
            stroke="#FFFEF4"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      );

    case tick:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="M11.9998 5.33337L7.33317 10L4.6665 7.33337"
            stroke="#767D85"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      );
    case rightArrow:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="19"
          height="8"
          viewBox="0 0 19 8"
          fill="none"
        >
          <path
            d="M0.5 3.18213C0.223858 3.18213 -2.41411e-08 3.40599 0 3.68213C2.41411e-08 3.95827 0.223858 4.18213 0.5 4.18213L0.5 3.68213L0.5 3.18213ZM18.3887 4.03568C18.584 3.84042 18.584 3.52384 18.3887 3.32857L15.2067 0.146594C15.0115 -0.0486685 14.6949 -0.0486684 14.4996 0.146594C14.3044 0.341856 14.3044 0.658438 14.4996 0.853701L17.328 3.68213L14.4996 6.51055C14.3044 6.70582 14.3044 7.0224 14.4996 7.21766C14.6949 7.41292 15.0115 7.41292 15.2067 7.21766L18.3887 4.03568ZM0.5 3.68213L0.5 4.18213L18.0352 4.18213L18.0352 3.68213L18.0352 3.18213L0.5 3.18213L0.5 3.68213Z"
            fill="#FFFEF4"
          />
        </svg>
      );

    case exploreRoundCta:
      return (
        <svg width="131" height="131" viewBox="0 0 131 131" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M64.8695 118.692L64.3093 127.97L57.7562 127.574L57.8181 126.549L63.2033 126.874L63.3804 123.942L58.4883 123.646L58.5478 122.66L63.4399 122.956L63.6366 119.698L58.0957 119.364L58.1607 118.287L64.8695 118.692Z" fill="white" />
          <path d="M54.8936 117.689L50.5497 121.452L52.2658 126.607L50.9597 126.258L49.6975 122.072L49.6724 122.065L46.4387 125.049L45.1954 124.716L49.3097 121.107L47.5092 115.713L48.8655 116.076L50.1652 120.474L50.1903 120.481L53.6126 117.346L54.8936 117.689Z" fill="white" />
          <path d="M44.0106 114.197L39.92 122.543L36.4413 120.838C35.5697 120.411 35.0398 119.818 34.8516 119.06C34.6916 118.441 34.7642 117.821 35.0694 117.198C35.4508 116.42 35.972 115.927 36.6332 115.72C37.2943 115.514 38.0724 115.63 38.9673 116.068L41.2437 117.184L42.96 113.682L44.0106 114.197ZM39.3157 121.118L40.826 118.036L38.6314 116.96C38.0555 116.678 37.5569 116.603 37.1354 116.734C36.7062 116.861 36.3715 117.17 36.1312 117.661C35.8871 118.159 35.85 118.609 36.0198 119.01C36.1781 119.416 36.5257 119.75 37.0627 120.014L39.3157 121.118Z" fill="white" />
          <path d="M35.2749 109.349L29.6992 116.786L28.7631 116.084L33.6915 109.51L29.791 106.586L30.4383 105.723L35.2749 109.349Z" fill="white" />
          <path d="M21.8051 98.2139C23.0321 98.2332 24.0998 98.761 25.0081 99.7972C25.9165 100.833 26.3027 101.964 26.1669 103.19C26.0253 104.409 25.4233 105.484 24.361 106.415C23.2987 107.346 22.154 107.802 20.927 107.783C19.6943 107.757 18.6237 107.226 17.7154 106.19C16.807 105.154 16.4236 104.026 16.5652 102.807C16.7011 101.582 17.3002 100.503 18.3625 99.572C19.4248 98.6408 20.5723 98.1881 21.8051 98.2139ZM25.0624 103.034C25.2083 102.123 24.9328 101.269 24.2358 100.474C23.5389 99.6791 22.7289 99.2942 21.8061 99.3194C20.8832 99.3447 19.9981 99.7287 19.1509 100.471C18.3036 101.214 17.807 102.041 17.6611 102.953C17.5087 103.87 17.7809 104.726 18.4779 105.521C19.1749 106.317 19.988 106.699 20.9174 106.668C21.8403 106.642 22.7254 106.258 23.5726 105.516C24.4199 104.773 24.9165 103.946 25.0624 103.034Z" fill="white" />
          <path d="M20.0828 93.4878L11.9493 97.9871L9.84751 94.1877C9.45317 93.4748 9.3391 92.7852 9.5053 92.1188C9.6673 91.4448 10.082 90.9232 10.7493 90.5541C11.7883 89.9793 12.7196 90.0782 13.5431 90.8508L13.5772 90.8319C13.499 90.3503 13.5692 89.94 13.7878 89.6012C13.9947 89.259 14.3985 88.8969 14.9994 88.515C15.9964 87.8644 16.5074 87.4184 16.5322 87.1768L16.5777 87.1516L17.1881 88.2551C17.166 88.3762 17.0555 88.5166 16.8565 88.6762C16.6534 88.8282 16.2118 89.122 15.5316 89.5577C14.9611 89.9228 14.6258 90.3014 14.5258 90.6935C14.4215 91.078 14.5225 91.5471 14.8288 92.1007L16.081 94.3644L19.5164 92.464L20.0828 93.4878ZM12.2702 96.4724L15.2506 94.8238L13.9165 92.4122C13.6061 91.851 13.2423 91.4976 12.825 91.352C12.4002 91.2107 11.9526 91.2701 11.4824 91.5302C10.9743 91.8112 10.6703 92.1478 10.5702 92.5399C10.4702 92.932 10.5817 93.42 10.9047 94.004L12.2702 96.4724Z" fill="white" />
          <path d="M15.3755 83.8749L6.50735 86.6594L4.54067 80.3959L5.52051 80.0883L7.13668 85.2355L9.93976 84.3554L8.47157 79.6795L9.41419 79.3835L10.8824 84.0594L13.9955 83.0819L12.3326 77.7858L13.3621 77.4626L15.3755 83.8749Z" fill="white" />
          <path d="M12.0263 66.5388L2.73986 66.1403L2.80396 64.6467L10.6482 61.9645L10.6493 61.9386L3.06258 58.6202L3.12779 57.1006L12.4142 57.4992L12.3663 58.6161L4.72934 58.2884L4.72823 58.3144C5.20605 58.4997 5.58975 58.655 5.87931 58.7801L12.2381 61.6034L12.1997 62.4996L5.62236 64.7676L4.4356 65.1331L4.43449 65.1591L12.0715 65.4868L12.0263 66.5388Z" fill="white" />
          <path d="M14.0183 46.9114C14.6749 47.9481 14.819 49.1303 14.4505 50.4581C14.0819 51.7859 13.348 52.729 12.2486 53.2873C11.1516 53.8373 9.92245 53.9234 8.56124 53.5456C7.20003 53.1678 6.19109 52.4605 5.53443 51.4238C4.88009 50.3787 4.73718 49.1923 5.10571 47.8645C5.47424 46.5367 6.20703 45.5978 7.30408 45.0478C8.40344 44.4895 9.63373 44.3992 10.9949 44.7771C12.3561 45.1549 13.3639 45.8663 14.0183 46.9114ZM11.7731 52.2784C12.6155 51.9006 13.1781 51.2023 13.4609 50.1835C13.7436 49.1646 13.6214 48.2763 13.0943 47.5183C12.5671 46.7604 11.7607 46.2308 10.6751 45.9295C9.58945 45.6282 8.62544 45.6664 7.78305 46.0442C6.93231 46.4197 6.36555 47.1169 6.08278 48.1357C5.8 49.1545 5.92638 50.044 6.4619 50.8043C6.98907 51.5622 7.79547 52.0918 8.8811 52.3931C9.96672 52.6945 10.9307 52.6562 11.7731 52.2784Z" fill="white" />
          <path d="M17.0213 42.8806L8.79903 38.5457L10.824 34.7048C11.2039 33.9842 11.7181 33.5107 12.3665 33.2843C13.019 33.0502 13.6825 33.111 14.3572 33.4667C15.4075 34.0204 15.8354 34.8534 15.6408 35.9658L15.6753 35.9839C16.0352 35.6544 16.4168 35.4882 16.82 35.4852C17.2196 35.4706 17.7439 35.6098 18.3927 35.9029C19.4835 36.38 20.1366 36.5627 20.3523 36.451L20.3983 36.4753L19.8102 37.5907C19.6967 37.6387 19.5187 37.6232 19.2762 37.5443C19.0377 37.4578 18.5498 37.2496 17.8126 36.9197C17.1944 36.6428 16.6939 36.5699 16.3112 36.7013C15.9325 36.8249 15.5956 37.1666 15.3005 37.7262L14.094 40.0147L17.567 41.8456L17.0213 42.8806ZM10.2416 37.9836L13.2546 39.5721L14.5399 37.1342C14.839 36.5668 14.935 36.0688 14.8279 35.6401C14.7132 35.2073 14.4181 34.8656 13.9428 34.615C13.4292 34.3442 12.981 34.2744 12.5982 34.4058C12.2155 34.5371 11.8685 34.8979 11.5573 35.4882L10.2416 37.9836Z" fill="white" />
          <path d="M22.4841 33.6756L15.293 27.7862L19.4527 22.7072L20.2473 23.3579L16.8289 27.5317L19.1019 29.3933L22.2072 25.6016L22.9716 26.2277L19.8662 30.0193L22.3907 32.0868L25.9079 27.7923L26.7426 28.476L22.4841 33.6756Z" fill="white" />
          <path d="M39.7981 14.7647L39.2286 13.6106L40.3594 13.0526L40.9289 14.2067L39.7981 14.7647Z" fill="white" />
          <path d="M61.0026 12.1923L60.7036 2.90215L67.2652 2.69101L67.2983 3.71748L61.9061 3.89099L62.0006 6.82747L66.899 6.66985L66.9308 7.65734L62.0323 7.81496L62.1373 11.0763L67.6854 10.8977L67.7201 11.9762L61.0026 12.1923Z" fill="white" />
          <path d="M71.0297 12.27L75.0076 8.12159L72.8226 3.14659L74.1554 3.37383L75.7988 7.42567L75.8244 7.43004L78.7688 4.16041L80.0375 4.37672L76.2741 8.35072L78.565 13.5548L77.181 13.3188L75.4807 9.05945L75.455 9.05508L72.3369 12.4929L71.0297 12.27Z" fill="white" />
          <path d="M82.1875 14.7414L85.4899 6.05282L89.1111 7.42917C90.0185 7.77403 90.6008 8.31525 90.8583 9.05282C91.0747 9.65429 91.0597 10.2791 90.8134 10.9272C90.5055 11.7373 90.0319 12.2758 89.3927 12.5428C88.7535 12.8098 87.9681 12.7663 87.0364 12.4122L84.6668 11.5115L83.2812 15.1571L82.1875 14.7414ZM86.2233 7.41634L85.004 10.6244L87.2885 11.4927C87.888 11.7206 88.3915 11.7497 88.799 11.5801C89.2146 11.4136 89.5194 11.0751 89.7134 10.5647C89.9105 10.0462 89.9059 9.59482 89.6997 9.21048C89.5046 8.82111 89.1276 8.5202 88.5686 8.30774L86.2233 7.41634Z" fill="white" />
          <path d="M91.3323 18.759L96.1974 10.8389L97.1944 11.4513L92.894 18.452L97.0479 21.0036L96.4831 21.923L91.3323 18.759Z" fill="white" />
          <path d="M105.775 28.6079C104.551 28.702 103.439 28.2751 102.439 27.3271C101.439 26.3792 100.95 25.2888 100.972 24.056C101.001 22.8292 101.501 21.7031 102.473 20.6778C103.444 19.6524 104.542 19.0927 105.766 18.9986C106.995 18.9105 108.11 19.3403 109.111 20.2883C110.111 21.2362 110.597 22.3236 110.568 23.5504C110.546 24.7833 110.049 25.9123 109.077 26.9376C108.106 27.963 107.005 28.5197 105.775 28.6079ZM102.086 24.1089C102.025 25.0301 102.378 25.8544 103.146 26.5817C103.913 27.3091 104.755 27.6175 105.672 27.5071C106.588 27.3967 107.434 26.9327 108.209 26.1149C108.984 25.2972 109.402 24.4277 109.464 23.5065C109.531 22.579 109.18 21.7516 108.413 21.0242C107.646 20.2969 106.801 19.9916 105.878 20.1083C104.961 20.2187 104.116 20.6827 103.341 21.5005C102.566 22.3182 102.147 23.1877 102.086 24.1089Z" fill="white" />
          <path d="M107.926 33.1532L115.61 27.922L118.053 31.5111C118.512 32.1845 118.689 32.8606 118.585 33.5395C118.486 34.2256 118.121 34.7832 117.491 35.2125C116.509 35.8807 115.573 35.8682 114.682 35.175L114.649 35.1969C114.772 35.6693 114.74 36.0842 114.553 36.4418C114.379 36.8017 114.01 37.1995 113.447 37.6353C112.515 38.3752 112.047 38.8665 112.045 39.1093L112.002 39.1386L111.292 38.0962C111.303 37.9735 111.4 37.8235 111.583 37.6463C111.772 37.4762 112.184 37.1428 112.821 36.6462C113.355 36.23 113.654 35.822 113.718 35.4223C113.786 35.0299 113.642 34.5721 113.286 34.0492L111.83 31.9108L108.585 34.1203L107.926 33.1532ZM115.43 29.4598L112.615 31.3767L114.166 33.6548C114.527 34.1849 114.922 34.5032 115.35 34.6095C115.787 34.7111 116.227 34.6106 116.671 34.3082C117.151 33.9814 117.422 33.6182 117.486 33.2185C117.549 32.8189 117.393 32.3433 117.018 31.7916L115.43 29.4598Z" fill="white" />
          <path d="M113.499 42.2917L122.072 38.7001L124.609 44.7552L123.662 45.1521L121.577 40.1761L118.867 41.3113L120.761 45.8317L119.85 46.2134L117.956 41.6931L114.946 42.9539L117.091 48.0737L116.096 48.4907L113.499 42.2917Z" fill="white" />
          <path d="M118.437 59.2421L127.72 58.7814L127.795 60.2745L120.232 63.6696L120.233 63.6956L128.094 66.2991L128.169 67.8182L118.885 68.279L118.83 67.1624L126.465 66.7835L126.463 66.7575C125.97 66.6171 125.574 66.4979 125.274 66.4L118.682 64.176L118.637 63.2801L124.977 60.4144L126.125 59.9409L126.124 59.9149L118.489 60.2938L118.437 59.2421Z" fill="white" />
          <path d="M118.267 78.9675C117.518 77.9958 117.265 76.8319 117.509 75.4758C117.754 74.1196 118.397 73.1128 119.441 72.4553C120.482 71.8064 121.698 71.6072 123.088 71.8577C124.479 72.1081 125.549 72.7192 126.298 73.6908C127.046 74.671 127.298 75.8392 127.054 77.1953C126.809 78.5515 126.166 79.5541 125.125 80.203C124.082 80.8605 122.865 81.0639 121.475 80.8135C120.085 80.563 119.015 79.9476 118.267 78.9675ZM120.007 73.416C119.203 73.87 118.708 74.6173 118.52 75.6579C118.333 76.6984 118.536 77.5717 119.131 78.2777C119.726 78.9837 120.578 79.4366 121.687 79.6364C122.796 79.8362 123.752 79.7091 124.556 79.2551C125.368 78.8026 125.868 78.0561 126.056 77.0156C126.243 75.975 126.035 75.1009 125.432 74.3934C124.837 73.6874 123.985 73.2345 122.876 73.0347C121.767 72.8349 120.811 72.962 120.007 73.416Z" fill="white" />
          <path d="M115.647 83.2594L124.234 86.8164L122.572 90.8279C122.261 91.5806 121.792 92.0995 121.168 92.3848C120.54 92.6782 119.873 92.6789 119.169 92.387C118.072 91.9327 117.569 91.1427 117.66 90.0172L117.624 90.0023C117.296 90.3636 116.931 90.5644 116.53 90.6045C116.133 90.656 115.598 90.5658 114.925 90.3339C113.795 89.9596 113.128 89.838 112.924 89.9691L112.876 89.9492L113.358 88.7842C113.467 88.7259 113.645 88.7249 113.894 88.7811C114.14 88.8452 114.645 89.0075 115.409 89.2679C116.05 89.4865 116.555 89.5128 116.924 89.3467C117.29 89.1886 117.594 88.8173 117.836 88.2328L118.826 85.8427L115.199 84.3403L115.647 83.2594ZM122.849 87.5093L119.703 86.2059L118.648 88.7521C118.403 89.3446 118.353 89.8494 118.499 90.2664C118.653 90.6868 118.979 90.9998 119.475 91.2054C120.012 91.4276 120.464 91.4556 120.833 91.2895C121.202 91.1234 121.515 90.7321 121.77 90.1156L122.849 87.5093Z" fill="white" />
          <path d="M111.059 92.9341L118.763 98.1344L115.09 103.576L114.239 103.001L117.257 98.5296L114.822 96.8858L112.08 100.948L111.261 100.395L114.003 96.3331L111.298 94.5076L108.193 99.1085L107.298 98.5049L111.059 92.9341Z" fill="white" />
          <path d="M95.5622 113.36L96.2358 114.456L95.1614 115.116L94.4877 114.02L95.5622 113.36Z" fill="white" />
        </svg>
      );

    case close: return (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5.77778 5.77777L18.2222 18.2222" stroke="#323232" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M18.2222 5.77781L5.77778 18.2222" stroke="#323232" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
    )

    case infoRound:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            fill="none"
            d="M7.33789 7.49915H8.10488V10.3337"
            stroke="#323232"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            fill="none"
            d="M7.33203 10.3335H8.87223"
            stroke="#323232"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            fill="none"
            d="M8.06661 5.49619C8.06661 5.58828 7.99196 5.66293 7.89988 5.66293C7.80779 5.66293 7.73314 5.58828 7.73314 5.49619C7.73314 5.40411 7.80779 5.32946 7.89988 5.32946"
            stroke="#323232"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            fill="none"
            d="M7.89854 5.32945C7.99063 5.32945 8.06528 5.4041 8.06528 5.49619"
            stroke="#323232"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <rect
            fill="none"
            x="1.99609"
            y="1.99658"
            width="12.005"
            height="12.005"
            rx="5"
            stroke="#323232"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );

    case calendar:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            fill="none"
            d="M7.5 3V6"
            stroke="#0A0A0B"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            fill="none"
            d="M16.5 3V6"
            stroke="#0A0A0B"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <rect
            fill="none"
            x="3"
            y="4.5"
            width="18"
            height="16.5"
            rx="3"
            stroke="#0A0A0B"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            fill="none"
            d="M13.3203 16H16.0003"
            stroke="#0A0A0B"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            fill="none"
            d="M14.6592 16V11L13.1992 12.32"
            stroke="#0A0A0B"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            fill="none"
            d="M8 11.96C8.15305 11.3968 8.66238 11.0044 9.246 11C9.97446 11 10.565 11.5905 10.565 12.319C10.5648 12.9712 10.0362 13.4998 9.384 13.5C10.0362 13.5002 10.5648 14.0288 10.565 14.681C10.565 15.4095 9.97446 16 9.246 16C8.66238 15.9956 8.15305 15.6032 8 15.04"
            stroke="#0A0A0B"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );

    case plus:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M12 5V19"
            stroke="#0A0A0B"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M5 12H19"
            stroke="#0A0A0B"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );

    // Uses currentColor so the parent (e.g. a password toggle button) controls
    // the icon color and its hover/focus states.
    case eye:
      return (
        <svg
          className={classes}
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          {...roleInfo}
        >
          <path
            d="M2.25 12C3.75 7.5 7.5 5.25 12 5.25C16.5 5.25 20.25 7.5 21.75 12C20.25 16.5 16.5 18.75 12 18.75C7.5 18.75 3.75 16.5 2.25 12Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            fill='none'
            strokeLinejoin="round"
          />
          <path
            d="M12 14.75C13.5188 14.75 14.75 13.5188 14.75 12C14.75 10.4812 13.5188 9.25 12 9.25C10.4812 9.25 9.25 10.4812 9.25 12C9.25 13.5188 10.4812 14.75 12 14.75Z"
            stroke="currentColor"
            strokeWidth="1.5"
            fill='none'
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );

    case eyeOff:
      return (
        <svg
          className={classes}
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          {...roleInfo}
        >
          <path
            d="M9.9 5.47C10.58 5.32 11.28 5.25 12 5.25C16.5 5.25 20.25 7.5 21.75 12C21.25 13.51 20.5 14.74 19.56 15.7M6.22 6.72C4.4 7.94 3.06 9.7 2.25 12C3.75 16.5 7.5 18.75 12 18.75C13.79 18.75 15.42 18.39 16.83 17.71"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            fill='none'
            strokeLinejoin="round"
          />
          <path
            d="M10.06 10.06C9.56 10.56 9.25 11.24 9.25 12C9.25 13.52 10.48 14.75 12 14.75C12.76 14.75 13.44 14.44 13.94 13.94"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill='none'
          />
          <path
            d="M3.75 3.75L20.25 20.25"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            fill='none'
            strokeLinejoin="round"
          />
        </svg>
      );
    default:
      return (
        <svg
          className={classes}
          width="29"
          height="19"
          viewBox="0 0 29 19"
          xmlns="http://www.w3.org/2000/svg"
          {...roleInfo}
        >
          <g fill="none" fillRule="evenodd">
            <path
              d="M26.58 19H2.42A2.4004 2.4004 0 0 1 0 16.62V2.38A2.4 2.4 0 0 1 2.42 0h24.16A2.4004 2.4004 0 0 1 29 2.38v14.25c-.0165 1.3216-1.0984 2.3811-2.42 2.37zM10 5.83c0-.46-.35-.83-.78-.83H3.78c-.43 0-.78.37-.78.83v3.34c0 .46.35.83.78.83h5.44c.43 0 .78-.37.78-.83V5.83z"
              fill="#DADDE2"
            />
            <path
              d="M25 15h-3c-.65 0-1-.3-1-1s.35-1 1-1h3c.65 0 1 .3 1 1s-.35 1-1 1zm-6 0h-3c-.65 0-1-.3-1-1s.35-1 1-1h3c.65 0 1 .3 1 1s-.35 1-1 1zm-6 0h-3c-.65 0-1-.3-1-1s.35-1 1-1h3c.65 0 1 .3 1 1s-.35 1-1 1zm-6 0H4c-.65 0-1-.3-1-1s.35-1 1-1h3c.65 0 1 .3 1 1s-.35 1-1 1z"
              fill="#B2B6C1"
            />
          </g>
        </svg>
      );
  }
};

export default IconCard;
